import { Edit2, Plus, Loader2 } from 'lucide-react';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  addDashboard,
  initializeProjectDashboard,
  removeWidget as removeWidgetRedux,
  reorderWidgets,
  selectActiveDashboard,
  selectProjectDashboard,
  updateWidgetConfig,
  upsertWidget,
  WidgetInstance,
} from '../../store/slices/widgetsSlice';
import {
  useGetDashboardsQuery,
  useGetWidgetsQuery,
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
  useUpdateWidgetMutation,
  useUpdateLayoutMutation,
} from '../../services/dashboardApi';
import type {
  DashboardWidget,
  CreateWidgetRequest,
  DashboardLayout as DashboardLayoutType,
} from '../../types/dashboard';
import AddWidgetPanel from './AddWidgetPanel';
import BaseWidget from './BaseWidget';
import { getWidgetComponent, WIDGET_TYPES } from './widgetRegistry';

interface DashboardLayoutProps {
  scope: 'org' | 'project';
  projectId?: string;
  hideHeader?: boolean;
  dashboardId?: string;
}

// Helper functions to convert between size strings and layout objects
const sizeToLayout = (size: '1x1' | '2x1' | '2x2'): DashboardLayoutType => {
  switch (size) {
    case '1x1':
      return { x: 0, y: 0, w: 3, h: 4 };
    case '2x1':
      return { x: 0, y: 0, w: 6, h: 4 };
    case '2x2':
      return { x: 0, y: 0, w: 12, h: 4 };
    default:
      return { x: 0, y: 0, w: 3, h: 4 };
  }
};

const layoutToSize = (layout?: DashboardLayoutType): '1x1' | '2x1' | '2x2' => {
  if (!layout) return '1x1';
  if (layout.w >= 12) return '2x2';
  if (layout.w >= 6) return '2x1';
  return '1x1';
};

// Convert API widget to Redux-compatible format
const apiWidgetToInstance = (widget: DashboardWidget): WidgetInstance => ({
  id: widget._id,
  type: widget.type,
  title: widget.title,
  size: layoutToSize(widget.layout),
  position: { x: widget.layout?.x || 0, y: widget.layout?.y || 0 },
  config: widget.configJSON || {},
});

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  scope: _scope,
  projectId,
  hideHeader = false,
  dashboardId: propDashboardId,
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [showAddDashboard, setShowAddDashboard] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  // Drag and drop state
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [dragOverWidgetId, setDragOverWidgetId] = useState<string | null>(null);

  // Get dashboards from API
  const { data: dashboardsResponse, isLoading: dashboardsLoading } = useGetDashboardsQuery(
    projectId ? { projectId, scope: 'all' } : { scope: 'all' }
  );

  // Determine active dashboard ID
  const activeDashboardId = useMemo(() => {
    if (propDashboardId) return propDashboardId;
    if (!dashboardsResponse?.data?.length) return null;

    // For project scope, find dashboard matching projectId
    if (_scope === 'project' && projectId) {
      const projectDashboard = dashboardsResponse.data.find(d => d.projectId === projectId);
      return projectDashboard?._id || dashboardsResponse.data[0]?._id;
    }

    // For org scope, find the default or first dashboard
    const defaultDashboard = dashboardsResponse.data.find(d => d.isDefaultForUser);
    return defaultDashboard?._id || dashboardsResponse.data[0]?._id;
  }, [propDashboardId, dashboardsResponse, _scope, projectId]);

  // Get widgets from API for the active dashboard
  const { data: widgetsResponse, isLoading: widgetsLoading } = useGetWidgetsQuery(
    activeDashboardId || '',
    {
      skip: !activeDashboardId,
    }
  );

  // API Mutations
  const [createWidget, { isLoading: isCreatingWidget }] = useCreateWidgetMutation();
  const [deleteWidget, { isLoading: isDeletingWidget }] = useDeleteWidgetMutation();
  const [updateWidgetApi, { isLoading: isUpdatingWidget }] = useUpdateWidgetMutation();
  const [updateLayout, { isLoading: isUpdatingLayout }] = useUpdateLayoutMutation();

  // Get Redux dashboard state as fallback
  const reduxActiveDashboard = useAppSelector(selectActiveDashboard);
  const projectDashboard = useAppSelector(state =>
    projectId ? selectProjectDashboard(state, projectId) : null
  );

  // Initialize project dashboard if needed (fallback to Redux for initial state)
  useEffect(() => {
    if (
      _scope === 'project' &&
      projectId &&
      !projectDashboard &&
      !dashboardsResponse?.data?.length
    ) {
      dispatch(initializeProjectDashboard(projectId));
    }
  }, [_scope, projectId, projectDashboard, dashboardsResponse, dispatch]);

  // Get current dashboard info from API response
  const currentApiDashboard = useMemo(() => {
    if (!dashboardsResponse?.data || !activeDashboardId) return null;
    return dashboardsResponse.data.find(d => d._id === activeDashboardId);
  }, [dashboardsResponse, activeDashboardId]);

  // Convert API widgets to WidgetInstance format
  const widgets = useMemo((): WidgetInstance[] => {
    if (widgetsResponse?.data) {
      return widgetsResponse.data.map(apiWidgetToInstance);
    }
    // Fallback to Redux if API not available
    const reduxDashboard =
      _scope === 'project' && projectDashboard ? projectDashboard : reduxActiveDashboard;
    return reduxDashboard?.widgets || [];
  }, [widgetsResponse, _scope, projectDashboard, reduxActiveDashboard]);

  // Get existing widget types for the AddWidgetPanel
  const existingWidgetTypes = widgets.map(w => w.type);

  // Create dashboard name for display
  const dashboardName =
    currentApiDashboard?.name ||
    (_scope === 'project' ? 'Project Dashboard' : 'Organization Dashboard');
  const dashboardScope = currentApiDashboard?.projectId ? 'project' : 'org';

  const handleAddWidget = useCallback(
    async (widgetType: string, size: '1x1' | '2x1' | '2x2') => {
      if (!activeDashboardId) return;

      // Get the widget title from the registry
      const widgetDef = WIDGET_TYPES.find(w => w.id === widgetType);
      const title =
        widgetDef?.name ||
        widgetType.charAt(0).toUpperCase() + widgetType.slice(1).replace(/-/g, ' ');

      const widgetData: CreateWidgetRequest = {
        type: widgetType,
        title,
        configJSON: { scope: _scope, projectId },
        layout: sizeToLayout(size),
        order: widgets.length,
      };

      try {
        await createWidget({
          dashboardId: activeDashboardId,
          data: widgetData,
        }).unwrap();
        setShowAddWidget(false);
      } catch {
        // Fallback to Redux if API fails
        const reduxDashboard =
          _scope === 'project' && projectDashboard ? projectDashboard : reduxActiveDashboard;
        if (reduxDashboard) {
          const newWidget: WidgetInstance = {
            id: `widget-${Date.now()}`,
            type: widgetType,
            title,
            size,
            position: { x: 0, y: 0 },
            config: { scope: _scope, projectId },
          };
          dispatch(
            upsertWidget({
              dashboardId: reduxDashboard.id,
              widget: newWidget,
            })
          );
          setShowAddWidget(false);
        }
      }
    },
    [
      activeDashboardId,
      widgets.length,
      _scope,
      projectId,
      createWidget,
      projectDashboard,
      reduxActiveDashboard,
      dispatch,
    ]
  );

  const handleRemoveWidget = useCallback(
    async (widgetId: string) => {
      try {
        await deleteWidget({ id: widgetId, dashboardId: activeDashboardId || undefined }).unwrap();
      } catch {
        // Fallback to Redux if API fails
        const reduxDashboard =
          _scope === 'project' && projectDashboard ? projectDashboard : reduxActiveDashboard;
        if (reduxDashboard) {
          dispatch(
            removeWidgetRedux({
              dashboardId: reduxDashboard.id,
              widgetId,
            })
          );
        }
      }
    },
    [deleteWidget, activeDashboardId, _scope, projectDashboard, reduxActiveDashboard, dispatch]
  );

  const handleWidgetConfigChange = useCallback(
    async (widgetId: string, config: Record<string, any>) => {
      try {
        await updateWidgetApi({
          id: widgetId,
          data: { configJSON: config },
          dashboardId: activeDashboardId || undefined,
        }).unwrap();
      } catch {
        // Fallback to Redux
        const reduxDashboard =
          _scope === 'project' && projectDashboard ? projectDashboard : reduxActiveDashboard;
        if (reduxDashboard) {
          dispatch(
            updateWidgetConfig({
              dashboardId: reduxDashboard.id,
              widgetId,
              config,
            })
          );
        }
      }
    },
    [updateWidgetApi, activeDashboardId, _scope, projectDashboard, reduxActiveDashboard, dispatch]
  );

  // Resize handler
  const handleWidgetResize = useCallback(
    async (widgetId: string, newSize: '1x1' | '2x1' | '2x2') => {
      try {
        await updateWidgetApi({
          id: widgetId,
          data: { layout: sizeToLayout(newSize) },
          dashboardId: activeDashboardId || undefined,
        }).unwrap();
      } catch {
        // Fallback to Redux
        const reduxDashboard =
          _scope === 'project' && projectDashboard ? projectDashboard : reduxActiveDashboard;
        if (reduxDashboard) {
          const widget = widgets.find(w => w.id === widgetId);
          if (widget) {
            dispatch(
              upsertWidget({
                dashboardId: reduxDashboard.id,
                widget: { ...widget, size: newSize },
              })
            );
          }
        }
      }
    },
    [
      updateWidgetApi,
      activeDashboardId,
      widgets,
      _scope,
      projectDashboard,
      reduxActiveDashboard,
      dispatch,
    ]
  );

  // Drag and drop handlers
  const handleDragStart = useCallback((_e: React.DragEvent, widgetId: string) => {
    setDraggedWidgetId(widgetId);
  }, []);

  const handleDragOver = useCallback(
    (_e: React.DragEvent, widgetId: string) => {
      if (draggedWidgetId && widgetId !== draggedWidgetId) {
        setDragOverWidgetId(widgetId);
      }
    },
    [draggedWidgetId]
  );

  const handleDrop = useCallback(
    async (_e: React.DragEvent, targetWidgetId: string) => {
      if (!activeDashboardId || !draggedWidgetId || draggedWidgetId === targetWidgetId) {
        setDraggedWidgetId(null);
        setDragOverWidgetId(null);
        return;
      }

      const widgetsCopy = [...widgets];
      const draggedIndex = widgetsCopy.findIndex(w => w.id === draggedWidgetId);
      const targetIndex = widgetsCopy.findIndex(w => w.id === targetWidgetId);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        // Reorder widgets
        const [draggedWidget] = widgetsCopy.splice(draggedIndex, 1);
        widgetsCopy.splice(targetIndex, 0, draggedWidget);

        // Update via API
        try {
          await updateLayout({
            dashboardId: activeDashboardId,
            data: {
              widgets: widgetsCopy.map((w, index) => ({
                id: w.id,
                layout: sizeToLayout(w.size),
                order: index,
              })),
            },
          }).unwrap();
        } catch {
          // Fallback to Redux
          const reduxDashboard =
            _scope === 'project' && projectDashboard ? projectDashboard : reduxActiveDashboard;
          if (reduxDashboard) {
            dispatch(
              reorderWidgets({
                dashboardId: reduxDashboard.id,
                widgets: widgetsCopy,
              })
            );
          }
        }
      }

      setDraggedWidgetId(null);
      setDragOverWidgetId(null);
    },
    [
      activeDashboardId,
      draggedWidgetId,
      widgets,
      updateLayout,
      _scope,
      projectDashboard,
      reduxActiveDashboard,
      dispatch,
    ]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedWidgetId(null);
    setDragOverWidgetId(null);
  }, []);

  const handleAddDashboard = () => {
    if (!newDashboardName.trim()) return;

    dispatch(
      addDashboard({
        name: newDashboardName,
        scope: _scope,
        projectId,
        layout: { columns: 12, gap: 16 },
      })
    );

    setNewDashboardName('');
    setShowAddDashboard(false);
  };

  // Loading state
  const isLoading = dashboardsLoading || widgetsLoading;
  const isMutating = isCreatingWidget || isDeletingWidget || isUpdatingWidget || isUpdatingLayout;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <Loader2 className="h-6 w-6 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Check if we have any dashboard available
  const hasDashboard = activeDashboardId || widgets.length > 0;

  if (!hasDashboard) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <h3 className="text-base font-medium text-gray-900 mb-1">No Dashboard Available</h3>
          <p className="text-sm text-gray-500">Create a dashboard to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-6 py-4">
      {/* Loading overlay for mutations */}
      {isMutating && (
        <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-red-500 animate-spin" />
        </div>
      )}

      {/* Dashboard Header - Compact inline header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-gray-900">{dashboardName}</h2>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded uppercase">
            {dashboardScope === 'org' ? 'Organization' : 'Project'}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              isEditing
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Edit2 className="h-4 w-4" />
            <span>{isEditing ? 'Done' : 'Edit'}</span>
          </button>

          <button
            onClick={() => setShowAddWidget(true)}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Widget</span>
          </button>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div
        className="grid grid-cols-12 gap-4 auto-rows-min"
        onDragEnd={handleDragEnd}
        onDragLeave={() => setDragOverWidgetId(null)}
      >
        {widgets.map(widget => {
          const WidgetComponent = getWidgetComponent(widget.type);

          if (!WidgetComponent) {
            return (
              <div
                key={widget.id}
                className="col-span-6 lg:col-span-3 h-32 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center"
              >
                <span className="text-xs text-gray-400">Widget not found: {widget.type}</span>
              </div>
            );
          }

          return (
            <BaseWidget
              key={widget.id}
              widget={widget}
              onRemove={handleRemoveWidget}
              onConfigChange={handleWidgetConfigChange}
              onResize={handleWidgetResize}
              onDragStart={handleDragStart}
              onDragOver={e => handleDragOver(e, widget.id)}
              onDrop={handleDrop}
              isEditing={isEditing}
              isDragging={draggedWidgetId === widget.id}
              isDragOver={dragOverWidgetId === widget.id}
            >
              <WidgetComponent
                scope={_scope}
                projectId={projectId}
                config={widget.config}
                size={widget.size}
              />
            </BaseWidget>
          );
        })}

        {/* Empty State */}
        {widgets.length === 0 && (
          <div className="col-span-12 py-12 border border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50/50">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Plus className="h-5 w-5 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No widgets yet</h3>
              <p className="text-xs text-gray-500 mb-3">Add widgets to customize your dashboard</p>
              <button
                onClick={() => setShowAddWidget(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add Widget</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Widget Panel */}
      <AddWidgetPanel
        isOpen={showAddWidget}
        onClose={() => setShowAddWidget(false)}
        onAddWidget={handleAddWidget}
        scope={_scope}
        existingWidgetTypes={existingWidgetTypes}
      />

      {/* Add Dashboard Modal */}
      {showAddDashboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-heading-md font-semibold text-gray-900 mb-4">
              Create New Dashboard
            </h3>
            <input
              type="text"
              value={newDashboardName}
              onChange={e => setNewDashboardName(e.target.value)}
              placeholder="Dashboard name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary mb-4"
              autoFocus
            />
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddDashboard(false);
                  setNewDashboardName('');
                }}
                className="px-4 py-2 text-body-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDashboard}
                disabled={!newDashboardName.trim()}
                className="px-4 py-2 text-body-sm font-medium text-white bg-brand-primary hover:bg-brand-700 disabled:bg-gray-300 rounded-lg transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
